'use client';

import Link from 'next/link';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Box, Button, Container, Typography } from '@mui/material';

export default function NotFound() {
  return (
    <Container maxWidth="lg">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Floating background orbs */}
        <Box
          sx={{
            position: 'absolute',
            top: '20%',
            left: '10%',
            width: { xs: 200, md: 400 },
            height: { xs: 200, md: 400 },
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(102, 126, 234, 0.15) 0%, transparent 70%)',
            filter: 'blur(40px)',
            animation: 'float 8s ease-in-out infinite',
            '@keyframes float': {
              '0%, 100%': { transform: 'translate(0, 0)' },
              '50%': { transform: 'translate(30px, -30px)' },
            },
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: '20%',
            right: '10%',
            width: { xs: 250, md: 500 },
            height: { xs: 250, md: 500 },
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(118, 75, 162, 0.12) 0%, transparent 70%)',
            filter: 'blur(50px)',
            animation: 'float 10s ease-in-out infinite reverse',
          }}
        />

        {/* Content */}
        <Box
          sx={{
            position: 'relative',
            zIndex: 1,
            textAlign: 'center',
            maxWidth: 600,
            px: 3,
          }}
        >
          {/* Elegant 404 */}
          <Box
            sx={{
              mb: 4,
              position: 'relative',
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: '7rem', sm: '10rem', md: '12rem' },
                fontWeight: 200,
                letterSpacing: '0.05em',
                lineHeight: 1,
                background:
                  'linear-gradient(135deg, rgba(102, 126, 234, 0.8) 0%, rgba(118, 75, 162, 0.8) 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 0,
                fontFamily:
                  'var(--font-plex-sans), "IBM Plex Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
              }}
            >
              404
            </Typography>

            {/* Subtle underline */}
            <Box
              sx={{
                width: 120,
                height: 2,
                mx: 'auto',
                mt: 2,
                background:
                  'linear-gradient(90deg, transparent 0%, rgba(102, 126, 234, 0.6) 50%, transparent 100%)',
                animation: 'shimmer 3s ease-in-out infinite',
                '@keyframes shimmer': {
                  '0%, 100%': { opacity: 0.3 },
                  '50%': { opacity: 1 },
                },
              }}
            />
          </Box>

          {/* Title */}
          <Typography
            variant="h4"
            sx={{
              fontWeight: 300,
              mb: 2,
              color: 'text.primary',
              fontSize: { xs: '1.5rem', sm: '2rem', md: '2.25rem' },
              letterSpacing: '-0.01em',
            }}
          >
            Lost in the Markets
          </Typography>

          {/* Description */}
          <Typography
            variant="body1"
            sx={{
              color: 'text.secondary',
              mb: 6,
              fontSize: { xs: '0.95rem', sm: '1.05rem' },
              lineHeight: 1.7,
              fontWeight: 300,
              maxWidth: 450,
              mx: 'auto',
            }}
          >
            The page you're seeking doesn't exist. Perhaps it was a prediction that didn't come
            true.
          </Typography>

          {/* Elegant CTA Button */}
          <Button
            component={Link}
            href="/"
            endIcon={<ArrowForwardIcon />}
            sx={{
              py: 1.75,
              px: 4,
              borderRadius: 2,
              fontWeight: 500,
              fontSize: '1rem',
              textTransform: 'none',
              color: 'white',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              border: 'none',
              position: 'relative',
              overflow: 'hidden',
              transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: '-100%',
                width: '100%',
                height: '100%',
                background:
                  'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                transition: 'left 0.6s',
              },
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 12px 40px rgba(102, 126, 234, 0.35)',
                '&::before': {
                  left: '100%',
                },
              },
              '&:active': {
                transform: 'translateY(0)',
              },
            }}
          >
            Return Home
          </Button>
        </Box>
      </Box>
    </Container>
  );
}
